// ========= START: App.js (Corrected with Anti-Echo Logic) =========

// ========= START: App.js - Imports Section =========

import React, { useState, useEffect, useCallback, useRef } from 'react';
import './App.css';
import db from './content_db.json';
import { characters } from './character_db';
import ConversationWindow from './components/ConversationWindow';
import CharacterSelection from './components/CharacterSelection';
import SubtitleControls from './components/SubtitleControls';
import AvatarDisplay from './components/AvatarDisplay';
import WelcomeScreen from './components/WelcomeScreen';
import BpEchoMainMenu from './components/BpEchoMainMenu';
import GradeSelection from './components/GradeSelection';
// UsernameSetupScreen import removed
import { speak } from './utils/tts';
import { useSpeechRecognition } from './hooks/useSpeechRecognition';
import audioManager from './utils/audioManager';
import Background from './components/Background';

// ========= END: App.js - Imports Section =========

// ========= START: Helper Functions (Unchanged) =========
const getGreeting = () => {
  const hour = new Date().getHours();
  if (hour >= 5 && hour < 12) return 'Good morning';
  if (hour >= 12 && hour < 18) return 'Good afternoon';
  if (hour >= 18 && hour < 22) return 'Good evening';
  return 'Hello';
};

const capitalizeFirstLetter = (string) => {
  if (!string) return '';
  return string.charAt(0).toUpperCase() + string.slice(1);
};

const formatUserTranscript = (transcript) => {
  if (!transcript) return '';
  let formatted = transcript.trim();
  let transcriptLower = formatted.toLowerCase();

  const fillerWords = ['um', 'umm', 'uh', 'ah', 'hmm', 'eto', 'ehh'];
  let hasFiller = true;
  while (hasFiller) {
    hasFiller = false;
    for (const filler of fillerWords) {
      if (transcriptLower.startsWith(filler + ' ')) {
        formatted = formatted.substring(filler.length + 1).trim();
        transcriptLower = formatted.toLowerCase();
        hasFiller = true;
        break;
      }
    }
  }

  if (formatted.length === 0) return '';

  const splitPhrases = [
    "how about you", "what about you", "and you", "do you like",
    "what is", "what are", "what kind", "what season", "what color", "what sport",
    "how are you"
  ];

  for (const phrase of splitPhrases) {
    const index = transcriptLower.indexOf(phrase);
    if (index > 0) {
      let part1 = formatUserTranscript(formatted.substring(0, index));
      let part2 = formatUserTranscript(formatted.substring(index));
      const lastCharOfPart1 = part1.slice(-1);
      if (!['.', '?', '!'].includes(lastCharOfPart1)) {
        part1 += '.';
      }
      return `${part1} ${part2}`;
    }
  }

  formatted = formatted.charAt(0).toUpperCase() + formatted.slice(1);
  const lastChar = formatted.slice(-1);
  if (['.', '?', '!'].includes(lastChar)) {
    return formatted;
  }

  const questionWords = ['who', 'what', 'where', 'when', 'why', 'how', 'do', 'are', 'is', 'can', 'will'];
  const firstWord = formatted.split(' ')[0].toLowerCase();
  if (questionWords.includes(firstWord)) {
    formatted += '?';
  } else {
    formatted += '.';
  }
  return formatted;
};

const getRandomItem = (arr) => arr[Math.floor(Math.random() * arr.length)];
// ========= END: Helper Functions (Unchanged) =========


// ========= START: App Component =========

// ========= START: App.js - getInitialPhase Function =========
const getInitialPhase = () => {
  const hash = window.location.hash.substring(1);
  // REMOVED: All 'PRONUNCIATION_CHALLENGE' related phases
  const validPhases = ['WELCOME', 'BP_ECHO_MAIN_MENU', 'CHARACTER_SELECTION', 'GRADE_SELECTION', 'CONVERSATION', 'ENDED'];
  if (hash && validPhases.includes(hash)) {
    return hash;
  }
  return 'WELCOME';
};
// ========= END: App.js - getInitialPhase Function =========

function App() {

  // ========= START: App.js - State and Refs Initialization Section =========
  const [appPhase, setAppPhase] = useState('LOADING');
  const [playerData, setPlayerData] = useState(null);
  const [subtitleMode, setSubtitleMode] = useState('EN');
  const [conversationHistory, setConversationHistory] = useState([]);
  const [currentTurn, setCurrentTurn] = useState('NONE');
  const [errorCount, setErrorCount] = useState(0);
  const [context, setContext] = useState({});
  const [activeCharacter, setActiveCharacter] = useState(characters[0]);
  const [botState, setBotState] = useState('idle');
  const [selectedGrade, setSelectedGrade] = useState(null);
  const [isMuted, setIsMuted] = useState(audioManager.isMuted); // NEW: Mute state

  const speechRecognition = useSpeechRecognition();
  const { finalTranscript, resetTranscript } = speechRecognition;
  const silenceTimer = useRef(null);
  // ========= END: App.js - State and Refs Initialization Section =========


  const navigate = (newPhase) => {
    if (window.history.state?.phase === newPhase) return;
    if ((appPhase === 'CONVERSATION' || appPhase === 'ENDED') && newPhase !== 'ENDED') {
      setConversationHistory([]);
      setCurrentTurn('NONE');
      setContext({});
    }
    const hash = `#${newPhase}`;
    const pageTitle = `BP ECHO - ${newPhase}`;
    window.history.pushState({ phase: newPhase }, pageTitle, hash);
    setAppPhase(newPhase);
  };

  const handleBack = () => {
    // This function defines the behavior for the in-app back button located in the header.
    // The browser's own back button (mouse or UI) is handled by the 'popstate' listener in the useEffect hook.
    switch (appPhase) {
      // From the main menu of the BP Echo app, go back to the main Welcome screen.
      case 'BP_ECHO_MAIN_MENU':
        navigate('WELCOME');
        break;

      // From the "Choose Your Teacher" page, the user could have come from multiple locations (e.g., Main Menu, Grade Selection).
      // Using the browser's history is the most intuitive way to go back to the correct previous page.
      case 'CHARACTER_SELECTION':
        window.history.back();
        break;

      // From the Grade Selection screen, the user should always return to the BP Echo main menu.
      case 'GRADE_SELECTION':
        navigate('BP_ECHO_MAIN_MENU');
        break;

      // When in a conversation or after it has ended...
      case 'CONVERSATION':
      case 'ENDED':
        // If it was a Grade 5/6 unit chat, go back to the Unit Selection screen for that grade.
        if (context.grade === 'grade5' || context.grade === 'grade6') {
          navigate('GRADE_SELECTION');
        }
        // Otherwise, it was a Free Chat, so go back to the BP Echo main menu.
        else {
          navigate('BP_ECHO_MAIN_MENU');
        }
        break;

      // For any other page, use the default browser history behavior as a reliable fallback.
      default:
        window.history.back();
        break;
    }
  };

  const savePlayerData = (newPlayerData) => {
    try {
      localStorage.setItem('bpEchoPlayerData', JSON.stringify(newPlayerData));
    } catch (error) { console.error("Failed to save player data", error); }
  };

  // ========= START: App.js - handleSaveUsername Function (REPLACEMENT) =========
  const handleSaveUsername = (username) => {
    // MODIFIED: Removed level, xp, and unlock points.
    // The player data now focuses on identity and conversation history.
    const newPlayerData = {
      username: username,
      conversationStats: {},
    };
    setPlayerData(newPlayerData);
    savePlayerData(newPlayerData);
    navigate('WELCOME');
  };

  const handleConversationComplete = () => {
    if (!context || !playerData) return;
    let conversationKey;
    if (context.isFreeChat) {
      conversationKey = context.conversationId;
    } else {
      conversationKey = `${context.grade}_unit${context.unit}`;
    }
    if (!conversationKey) return;
    setPlayerData(prevData => {
      const newStats = { ...prevData.conversationStats };
      newStats[conversationKey] = (newStats[conversationKey] || 0) + 1;
      const newPlayerData = { ...prevData, conversationStats: newStats };
      savePlayerData(newPlayerData);
      return newPlayerData;
    });
  };

  const handleUnitSelect = (grade, unit) => {
    const gradeNumber = grade === 'grade5' ? 5 : 6;
    const firstBotMessage = {
      speaker: 'bot',
      english: `Welcome to Grade ${gradeNumber}, Unit ${unit}. Let's begin!`,
      spoken: false // MODIFICATION: Add spoken flag
    };
    setConversationHistory([firstBotMessage]);
    setContext({
      isFreeChat: false,
      grade: grade,
      unit: unit,
      step: 'START_UNIT'
    });
    setErrorCount(0);
    navigate('CONVERSATION');
  };

  const handleHint = () => {
    if (!context.isFreeChat) return;
    let hintText = '';
    switch (context.step) {
      case 'AWAITING_NAME': hintText = "Try saying: My name is..."; break;
      case 'AWAITING_SPELLING': hintText = `Try spelling: ${context.name.toUpperCase().split('').join(' ')}`; break;
      case 'AWAITING_FEELING': hintText = "Try saying: I'm good."; break;
      case 'AWAITING_LIKE': hintText = `Try saying: I like ${getRandomItem(db.freeChat.vocabulary[context.topic])}.`; break;
      case 'AWAITING_SECOND_LIKE': hintText = "Try saying: Yes, I do. or No, I don't."; break;
      case 'AWAITING_HOBBY': hintText = "Try saying: My hobby is reading."; break;
      case 'AWAITING_SECOND_HOBBY': hintText = "Try saying: Yes, I do. or No, I don't."; break;
      default: break;
    }
    if (hintText) {
      const botMessage = { speaker: 'bot', english: hintText, spoken: false }; // MODIFICATION: Add spoken flag
      setConversationHistory(prev => [...prev, botMessage]);
      setCurrentTurn('BOT_TURN');
    }
  };

  const handleAskNewQuestion = () => {
    let newBotQuestion = '';
    const newContext = { ...context };

    // Determine the next topic or move to next question
    if (context.isFreeChat) {
      if (context.conversationId === 'FC_Likes') {
        const otherTopics = db.freeChat.topics.filter(t => t !== context.topic);
        const newTopic = getRandomItem(otherTopics);
        newContext.topic = newTopic;
        newBotQuestion = `What ${newTopic} do you like?`;
        newContext.initial_question = newBotQuestion;
        newContext.step = 'AWAITING_LIKE'; // Reset step for new topic
      } else if (context.conversationId === 'FC_Hobbies') {
        const allHobbies = Object.values(db.freeChat.hobbies_categorized).flat();
        const nextHobby = getRandomItem(allHobbies.filter(h => h.gerund !== context.user_hobby && h.gerund !== context.bot_last_hobby_asked));
        newBotQuestion = `Do you like ${nextHobby.gerund}?`;
        newContext.bot_last_hobby_asked = nextHobby.gerund;
        newContext.step = 'AWAITING_SECOND_HOBBY';
      }
    }

    if (newBotQuestion) {
      const transitionMessage = getRandomItem(db.freeChat.skipAndRetryResponses);
      const botMessage = { speaker: 'bot', english: `${transitionMessage} ${newBotQuestion}`, spoken: false };
      setErrorCount(0);
      setContext(newContext);
      setConversationHistory(prev => [...prev, botMessage]);
      setCurrentTurn('BOT_TURN');
    } else {
      handleGiveUpAndEndTopic();
    }
  };

  const repeatLastBotMessage = () => {
    const lastBotMessage = conversationHistory.findLast(m => m.speaker === 'bot');
    if (lastBotMessage) {
      // If we have a specific initial question stored in context, use that for the reprompt.
      // This avoids repeating long greetings or transition phrases (e.g., "Oh, art? That sounds nice.")
      const textToRepeat = context.initial_question || lastBotMessage.english;
      const repeatMessage = { speaker: 'bot', english: textToRepeat, spoken: false };
      setConversationHistory(prev => [...prev, repeatMessage]);
      setCurrentTurn('BOT_TURN');
    }
  };

  const handleGiveUpAndEndTopic = () => {
    const giveUpMessage = getRandomItem(db.freeChat.finalGiveUpResponses);
    const botMessage = { speaker: 'bot', english: giveUpMessage, spoken: false }; // MODIFICATION: Add spoken flag
    setConversationHistory(prev => [...prev, botMessage]);
    setContext(prev => ({ ...prev, step: 'DONE' }));
    setCurrentTurn('BOT_TURN');
  };

  const handleSkip = () => {
    setErrorCount(0);
    setContext(prev => ({ ...prev, step: 'DONE' }));
    const userMessage = { speaker: 'user', english: '(Skipped)', japanese: '' };
    const botMessage = { speaker: 'bot', english: "Okay, let's talk next time!", spoken: false }; // MODIFICATION: Add spoken flag
    setConversationHistory(prev => [...prev, userMessage, botMessage]);
    setCurrentTurn('BOT_TURN');
  };

  const handleThemeSelect = (conversation) => {
    let newContext = { isFreeChat: true, conversationId: conversation.conversationId };
    let firstBotMessage = '';
    switch (conversation.conversationId) {
      case 'FC_Intro':
        firstBotMessage = `${getGreeting()}! My name is ${activeCharacter.name}. What’s your name?`;
        newContext.step = 'AWAITING_NAME';
        break;
      case 'FC_Likes':
        const randomTopic = getRandomItem(db.freeChat.topics);
        newContext.topic = randomTopic;
        const opener = getRandomItem(db.freeChat.likeDislikeOpeners);
        const initialQuestion = `What ${randomTopic} do you like?`;
        firstBotMessage = `${getGreeting()}! ${opener} ${initialQuestion}`;
        newContext.initial_question = initialQuestion;
        newContext.step = 'AWAITING_LIKE';
        break;
      case 'FC_Hobbies':
        const hobbyOpener = getRandomItem(db.freeChat.hobbyOpeners);
        firstBotMessage = `${getGreeting()}! ${hobbyOpener}`;
        newContext.step = 'AWAITING_HOBBY';
        break;
      default: return;
    }
    setContext(newContext);
    setConversationHistory([{ speaker: 'bot', english: firstBotMessage, spoken: false }]); // MODIFICATION: Add spoken flag
    setErrorCount(0);
    navigate('CONVERSATION');
  };

  const handleCharacterSelect = (character) => {
    setActiveCharacter(character);
    navigate('BP_ECHO_MAIN_MENU');
  };

  const showCharacterSelection = () => {
    navigate('CHARACTER_SELECTION');
  };

  // ========= START: App.js - New BGM Handlers Section =========
  const handleStartEcho = () => {
    audioManager.playBGM('chatBgmMenu');
    navigate('BP_ECHO_MAIN_MENU');
  };

  const handleToggleMute = () => {
    const newMuteState = audioManager.toggleMute();
    setIsMuted(newMuteState);
  };
  // ========= END: App.js - New BGM Handlers Section =========


  // ========= START: App.js - Initialization Effects (REPLACE FIRST 2 useEffects) =========
  // Replace the first two useEffect hooks (Load Player Data and PopState) with this block.

  useEffect(() => {
    try {
      const savedData = localStorage.getItem('bpEchoPlayerData');
      if (savedData) {
        const parsedData = JSON.parse(savedData);
        setPlayerData(parsedData);
        setAppPhase(getInitialPhase());
      } else {
        // MODIFIED: Auto-initialize "Guest" user immediately.
        // We no longer show the Username Setup screen.
        const defaultData = {
          username: 'Guest',
          conversationStats: {},
        };
        setPlayerData(defaultData);
        savePlayerData(defaultData);
        setAppPhase('WELCOME');
      }
    } catch (error) {
      console.error("Failed to load player data", error);
      // Fallback in case of error
      const defaultData = { username: 'Guest', conversationStats: {} };
      setPlayerData(defaultData);
      setAppPhase('WELCOME');
    }
  }, []);

  useEffect(() => {
    const handlePopState = (event) => {
      if (event.state && event.state.phase) {
        setAppPhase(event.state.phase);
      } else {
        // MODIFIED: Default fallback is now WELCOME, not USERNAME_SETUP
        setAppPhase('WELCOME');
      }
    };
    window.addEventListener('popstate', handlePopState);
    const initialPhase = getInitialPhase();
    const hash = `#${initialPhase}`;
    if (appPhase !== 'LOADING') {
      window.history.replaceState({ phase: initialPhase }, `BP ECHO - ${initialPhase}`, hash);
    }
    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [playerData, appPhase]);
  // ========= END: App.js - Initialization Effects =========

  // ========= START: App.js - BGM Management Effect Hook Section =========
  useEffect(() => {
    const chatMenuPhases = [
      'BP_ECHO_MAIN_MENU',
      'CHARACTER_SELECTION',
      'GRADE_SELECTION'
    ];

    if (chatMenuPhases.includes(appPhase)) {
      audioManager.playBGM('chatBgmMenu');
    } else {
      // For any other phase (like WELCOME, CONVERSATION, etc.), stop the music.
      audioManager.stopBGM();
    }
  }, [appPhase]);
  // ========= END: App.js - BGM Management Effect Hook Section =========

  useEffect(() => {
    /*
      =======================================================================
      === DEVELOPMENT MODE: AUTHORIZATION CHECK IS DISABLED ===============
      =======================================================================
      The code inside this useEffect hook is commented out to prevent the
      React app from redirecting to the login page. This allows you to
      work on and test the component directly.

      *** CRITICAL: Before deploying to the live server (bplabo.jp), you ***
      *** MUST UNCOMMENT this block to re-enable security in the app.    ***
      =======================================================================
    */

    const checkAuthorization = () => {
      if (window.auth && window.auth.isLoggedIn()) {
        const currentUser = window.auth.getCurrentUser();
        const allowedRoles = ['admin', 'alt', 'jte', 'user', 'student'];

        if (!currentUser || !allowedRoles.includes(currentUser.role)) {
          alert('Access Denied: You do not have permission to view this page.');
          window.location.href = process.env.NODE_ENV === 'development'
            ? 'http://localhost:8000'
            : '/';
        }
      } else {
        window.location.href = process.env.NODE_ENV === 'development'
          ? 'http://localhost:8000/login.html'
          : '/login.html';
      }
    };
    checkAuthorization();

  }, []); // The empty array ensures this effect runs only once on mount.

  useEffect(() => {
    // MODIFIED: Removed the condition for 'PRONUNCIATION_CHALLENGE'.
    if (appPhase !== 'CONVERSATION' && appPhase !== 'ENDED') {
      speechRecognition.stopListening();
    }
  }, [appPhase, speechRecognition]);

  useEffect(() => {
    if (appPhase === 'CONVERSATION' && conversationHistory.length > 0 && currentTurn === 'NONE') {
      const lastMessage = conversationHistory[conversationHistory.length - 1];
      if (lastMessage.speaker === 'bot') {
        setCurrentTurn('BOT_TURN');
      }
    }
  }, [appPhase, conversationHistory, currentTurn]);

  // ========= CRITICAL FIX: This is the updated useEffect for speaking =========
  useEffect(() => {
    const latestMessageIndex = conversationHistory.length - 1;
    if (latestMessageIndex < 0) return;

    const latestMessage = conversationHistory[latestMessageIndex];

    if (currentTurn === 'BOT_TURN' && appPhase === 'CONVERSATION' && latestMessage.speaker === 'bot' && !latestMessage.spoken) {
      // 1. Immediately mark the message as "spoken" in the state
      const newHistory = [...conversationHistory];
      newHistory[latestMessageIndex].spoken = true;
      setConversationHistory(newHistory);

      // 2. Play the audio
      setBotState('talking');
      speak(latestMessage.english, activeCharacter.voice, () => {
        setBotState('idle');
        if (appPhase === 'CONVERSATION') { // Check phase again in case user navigated away
          if (context.step === 'DONE') {
            handleConversationComplete();
            navigate('ENDED');
            setCurrentTurn('NONE');
          } else {
            setCurrentTurn('USER_TURN');
          }
        }
      });
    }
  }, [currentTurn, conversationHistory, activeCharacter, context, appPhase, navigate]);
  // ========= END CRITICAL FIX =========

  useEffect(() => {
    clearTimeout(silenceTimer.current);
    if (currentTurn === 'USER_TURN' && appPhase === 'CONVERSATION') {
      speechRecognition.startListening();
      silenceTimer.current = setTimeout(() => {
        if (appPhase === 'CONVERSATION' && currentTurn === 'USER_TURN') {
          speechRecognition.stopListening();
          setErrorCount(prev => prev + 1);
          setCurrentTurn('REPROMPTING');
        }
      }, 7000); // Increased silence timer slightly
    }
    return () => clearTimeout(silenceTimer.current);
  }, [currentTurn, appPhase, speechRecognition]);

  useEffect(() => {
    if (appPhase !== 'CONVERSATION') return;
    if (currentTurn === 'REPROMPTING') {
      if (errorCount === 1) {
        repeatLastBotMessage();
      } else if (errorCount === 2) {
        handleHint();
      } else {
        if (context.conversationId === 'FC_Intro') {
          handleGiveUpAndEndTopic();
        } else {
          handleAskNewQuestion();
        }
      }
      return;
    }
    if (!finalTranscript || currentTurn !== 'USER_TURN') {
      return;
    }
    clearTimeout(silenceTimer.current);
    speechRecognition.stopListening(); // Stop listening as soon as we have a final result

    let transcriptLower = finalTranscript.toLowerCase().trim();
    const fillerWords = ['um ', 'umm ', 'uh ', 'ah ', 'hmm ', 'eto ', 'ehh '];
    fillerWords.forEach(filler => {
      if (transcriptLower.startsWith(filler)) {
        transcriptLower = transcriptLower.substring(filler.length);
      }
    });
    const userMessage = { speaker: 'user', english: formatUserTranscript(finalTranscript), japanese: '' };
    setConversationHistory(prev => [...prev, userMessage]);

    // Check if it's a skip request
    if (transcriptLower.includes('skip please')) {
      handleSkip();
      resetTranscript();
      return;
    }

    // Check for user asking a question (e.g. "How about you?")
    const questionWords = ['who', 'what', 'where', 'when', 'why', 'how', 'do you'];
    const isUserQuestion = questionWords.some(word => transcriptLower.startsWith(word));
    const isExpectingAnswer = context.step && context.step !== 'DONE';
    if (isUserQuestion && isExpectingAnswer) {
      let botAnswer = "That's a good question.";
      if (transcriptLower.includes('how are you')) {
        botAnswer = getRandomItem(db.freeChat.botPositiveResponses);
      } else if (transcriptLower.includes('do you like')) {
        botAnswer = Math.random() > 0.5 ? "Yes, I do." : "No, not really.";
      }
      const botResponseText = `${botAnswer} Anyway, ${context.initial_question}`;
      const botMessage = { speaker: 'bot', english: botResponseText, spoken: false };
      setConversationHistory(prev => [...prev, botMessage]);
      setCurrentTurn('BOT_TURN');
      resetTranscript();
      return;
    }

    // Check for confusion or repeat request
    const isAwaitingPreference = context.step === 'AWAITING_SECOND_LIKE' || context.step === 'AWAITING_SECOND_HOBBY';
    const isConfusion = !isAwaitingPreference && (
      transcriptLower.includes("don't know") ||
      transcriptLower.includes("do not know") ||
      transcriptLower.includes("don't understand") ||
      transcriptLower.includes("do not understand")
    );

    if (isConfusion) {
      setErrorCount(prev => prev + 1);
      setCurrentTurn('REPROMPTING');
      resetTranscript();
      return;
    }

    // Process positive/negative intent
    const isPositive = [
      'yes', 'yeah', 'yup', 'sure', 'uh-huh', 'i do',
      'i love', 'i like', "it's fun", "it's great", 'i really like'
    ].some(pos => transcriptLower.includes(pos));

    const isNegative = [
      'no', 'nah', 'nope', 'uh-un', 'not really', "don't",
      "don't know", "do not know", "don't like", "do not like",
      "don't play", "do not play", "can't play", "cannot play"
    ].some(neg => transcriptLower.includes(neg));

    // STANDALONE REPEAT CHECK: Only trigger if no other intent was found.
    const isRepeatRequestStandalone = db.freeChat.repeatRequestPhrases.some(phrase => {
      // If it's "what", only count as repeat if it's very short (don't capture "What about you?")
      if (phrase === 'what') {
        const words = transcriptLower.split(/\s+/);
        return words.length <= 2 && words.includes('what');
      }
      return transcriptLower.includes(phrase);
    });

    if (isRepeatRequestStandalone && !isPositive && !isNegative) {
      repeatLastBotMessage();
      resetTranscript();
      return;
    }

    let newContext = { ...context };
    let botResponseText = '';
    let wasSuccessful = true;
    const hasFollowUp = db.freeChat.followUpQuestions_Simple.some(phrase => transcriptLower.includes(phrase));

    switch (context.conversationId) {
      case 'FC_Intro':
        switch (context.step) {
          case 'AWAITING_NAME': {
            const nameMatch = finalTranscript.toLowerCase().match(/(?:my name is|i'm|i am)\s+(\w+)/);
            const name = nameMatch ? capitalizeFirstLetter(nameMatch[1]) : capitalizeFirstLetter(finalTranscript.split(' ').pop());
            newContext.name = name;
            botResponseText = `It's nice to meet you, ${name}! Could you spell that for me?`;
            newContext.step = 'AWAITING_SPELLING';
            newContext.initial_question = 'Could you spell that for me?';
            break;
          }
          case 'AWAITING_SPELLING': {
            const expectedAnswer = context.name.toLowerCase();
            const userSpelling = finalTranscript.replace(/[\s.-]/g, '').toLowerCase();
            if (userSpelling === expectedAnswer) {
              setConversationHistory(prev => {
                const newHistory = [...prev];
                newHistory[newHistory.length - 1].english = finalTranscript.replace(/\s/g, '').split('').join(' ').toUpperCase();
                return newHistory;
              });
              botResponseText = "Got it. Thank you! How are you?";
              newContext.step = 'AWAITING_FEELING';
              newContext.initial_question = "How are you?";
            } else {
              const newErrorCount = errorCount + 1;
              if (newErrorCount < 3) {
                setErrorCount(newErrorCount);
                const reprompt = getRandomItem(db.freeChat.shortReprompts);
                botResponseText = `${reprompt} How do you spell ${context.name}?`;
                newContext.isSoftFailure = true;
              } else {
                wasSuccessful = false;
              }
            }
            break;
          }
          case 'AWAITING_FEELING': {
            const positiveFeelings = ['good', 'great', 'happy', 'fine'];
            const negativeSadFeelings = ['sad', 'angry', 'tired'];
            const negativeSickFeelings = ['sick', 'stomachache', 'headache'];
            const temperatureHot = ['hot'];
            const temperatureCold = ['cold', 'chilly'];
            if (positiveFeelings.some(feeling => transcriptLower.includes(feeling))) {
              botResponseText = getRandomItem(db.freeChat.feelingResponses.positive);
            } else if (negativeSadFeelings.some(feeling => transcriptLower.includes(feeling))) {
              botResponseText = getRandomItem(db.freeChat.feelingResponses.negative_sad);
            } else if (negativeSickFeelings.some(feeling => transcriptLower.includes(feeling))) {
              botResponseText = getRandomItem(db.freeChat.feelingResponses.negative_sick);
            } else if (temperatureHot.some(feeling => transcriptLower.includes(feeling))) {
              botResponseText = getRandomItem(db.freeChat.feelingResponses.temperature_hot);
            } else if (temperatureCold.some(feeling => transcriptLower.includes(feeling))) {
              botResponseText = getRandomItem(db.freeChat.feelingResponses.temperature_cold);
            } else {
              botResponseText = "I see.";
            }
            if (hasFollowUp) {
              botResponseText += " " + getRandomItem(db.freeChat.botPositiveResponses);
            }
            newContext.step = 'DONE';
            break;
          }
          default:
            break;
        }
        break;
      case 'FC_Likes':
      case 'FC_Hobbies':
        const isLikesTopic = context.conversationId === 'FC_Likes';
        const currentStep = context.step;
        if ((isLikesTopic && currentStep === 'AWAITING_LIKE') || (!isLikesTopic && currentStep === 'AWAITING_HOBBY')) {
          // Check for Category Rejection (e.g., "I don't play sports")
          const categoryKeyword = isLikesTopic ? context.topic : (context.topic || 'hobbies');
          const isCategoryRejection = isNegative && transcriptLower.includes(categoryKeyword);

          if (isCategoryRejection) {
            handleAskNewQuestion("Oh, I see. Then let's talk about something else!");
            return;
          }

          const match = transcriptLower.match(/(?:i like|my hobby is|i enjoy|i really like|i love|i enjoy)\s+(.+)/);
          let userResponseItem = match ? match[1].replace(/[.?]$/, '') : transcriptLower;

          // CLEANUP: Strip follow-up questions (e.g., "How about you?") from the extracted item
          db.freeChat.followUpQuestions_Simple.forEach(phrase => {
            const phraseIndex = userResponseItem.toLowerCase().indexOf(phrase.toLowerCase());
            if (phraseIndex !== -1) {
              userResponseItem = userResponseItem.substring(0, phraseIndex).trim();
            }
          });
          // Final trim and punctuation cleanup
          userResponseItem = userResponseItem.replace(/[.,?!]$/, '').trim();

          newContext.user_response = userResponseItem;

          let botFollowUpText = '';
          if (isNegative) {
            botFollowUpText = `I see. You don't like ${userResponseItem}? That's okay. `;
          } else {
            botFollowUpText = `Oh, ${userResponseItem}? That sounds nice. `;
          }

          let nextQuestion = '';
          if (isLikesTopic) {
            const vocab = db.freeChat.vocabulary[context.topic];
            const newItem = getRandomItem(vocab.filter(item => item.toLowerCase() !== userResponseItem.toLowerCase()));
            nextQuestion = `Do you like ${newItem}?`;
            newContext.bot_last_item_asked = newItem;
            newContext.step = 'AWAITING_SECOND_LIKE';
          } else {
            const allHobbies = Object.values(db.freeChat.hobbies_categorized).flat();
            const nextHobby = getRandomItem(allHobbies.filter(h => h.gerund.toLowerCase() !== userResponseItem.toLowerCase()));
            nextQuestion = `Do you like ${nextHobby.gerund}?`;
            newContext.bot_last_hobby_asked = nextHobby.gerund;
            newContext.step = 'AWAITING_SECOND_HOBBY';
          }
          botResponseText = botFollowUpText + nextQuestion;
          newContext.initial_question = nextQuestion;
        } else if ((isLikesTopic && currentStep === 'AWAITING_SECOND_LIKE') || (!isLikesTopic && currentStep === 'AWAITING_SECOND_HOBBY')) {
          const lastAskedItem = isLikesTopic ? context.bot_last_item_asked : context.bot_last_hobby_asked;
          if (isPositive || isNegative) {
            if (isNegative && hasFollowUp) {
              botResponseText = `I see. Well, to answer your question, I do like ${lastAskedItem}.`;
            } else if (hasFollowUp) {
              const botLikesIt = Math.random() > 0.4;
              botResponseText = botLikesIt
                ? `Yes, I like ${lastAskedItem} too. Thanks for asking!`
                : `I don't really like ${lastAskedItem}, but that's okay!`;
            } else {
              botResponseText = isNegative ? "I see. Thanks for sharing!" : "That's great. Thanks for sharing!";
            }
            newContext.step = 'DONE';
          } else {
            wasSuccessful = false;
          }
        }
        break;
      default:
        wasSuccessful = false;
        break;
    }
    if (wasSuccessful) {
      if (!newContext.isSoftFailure) {
        setErrorCount(0);
      } else {
        delete newContext.isSoftFailure;
      }
      setContext(newContext);
      const botMessage = { speaker: 'bot', english: botResponseText, spoken: false }; // MODIFICATION: Add spoken flag
      setConversationHistory(prev => [...prev, botMessage]);
      setCurrentTurn('BOT_TURN');
    } else {
      setErrorCount(prev => prev + 1);
      setCurrentTurn('REPROMPTING');
    }
    resetTranscript();
  }, [finalTranscript, currentTurn, context, errorCount, resetTranscript, appPhase]);
  // ========= END: App.js - Effect Hooks Section (UPDATED) =========


  // ========= START: App.js - Render Logic Section (REPLACEMENT) =========
  const renderContent = () => {
    if (appPhase === 'LOADING') {
      return <div>Loading BP ECHO...</div>;
    }
    // REMOVED: USERNAME_SETUP case

    if (appPhase === 'WELCOME') {
      const welcomeBackground = { type: 'video', src: `${process.env.PUBLIC_URL}/assets/backgrounds/welcome-bg.mp4` };
      return <WelcomeScreen
        onStartEcho={handleStartEcho}
        background={welcomeBackground}
      />;
    }

    if (appPhase === 'CHARACTER_SELECTION') {
      return <CharacterSelection onCharacterSelect={handleCharacterSelect} />;
    }
    if (appPhase === 'BP_ECHO_MAIN_MENU') {
      return <BpEchoMainMenu
        onStartFreeChat={(topic) => {
          const conversationIdMap = {
            introductions: "FC_Intro", 'likes-dislikes': "FC_Likes", hobbies: "FC_Hobbies"
          };
          const conversation = { conversationId: conversationIdMap[topic] };
          if (conversation.conversationId) {
            handleThemeSelect(conversation);
          }
        }}
        onConversationPractice={(grade) => {
          setSelectedGrade(grade);
          navigate('GRADE_SELECTION');
        }}
      />;
    }
    if (appPhase === 'GRADE_SELECTION') {
      return <GradeSelection grade={selectedGrade} onUnitSelect={handleUnitSelect} onBack={handleBack} />;
    }
    return ( // CONVERSATION or ENDED
      <>
        <AvatarDisplay botState={botState} activeCharacter={activeCharacter} />
        <div className="conversation-container">
          <ConversationWindow history={conversationHistory} subtitleMode={subtitleMode} />
          <div className="controls-footer">
            {appPhase === 'ENDED' ? (
              <div className="end-screen"><p>Conversation Complete!</p><button onClick={handleBack} className="start-button">Back to Menu</button></div>
            ) : (
              <>
                <SubtitleControls mode={subtitleMode} setMode={setSubtitleMode} />
                {speechRecognition.isListening && <div className="listening-indicator">🎙️ Listening...</div>}
                {(currentTurn === 'USER_TURN' || currentTurn === 'REPROMPTING') && (
                  <div className="hint-container">
                    <button onClick={handleHint} className="hint-button">Hint</button>
                    <button onClick={handleSkip} className="hint-button skip-button">Skip</button>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </>
    );
  };
  // ========= END: App.js - Render Logic Section (REPLACEMENT) =========

  // ========= START: App.js - getBackgroundConfig Function (REPLACEMENT) =========
  const getBackgroundConfig = () => {
    switch (appPhase) {
      // These phases manage their own backgrounds or have none.
      case 'WELCOME':
        return { type: 'none' };
      // REMOVED: USERNAME_SETUP case

      // Use a video for the main menu.
      case 'BP_ECHO_MAIN_MENU':
        return { type: 'video', src: `${process.env.PUBLIC_URL}/assets/backgrounds/menu-bg.mp4` };

      // UPDATED: Use specific videos for each grade selection screen.
      case 'GRADE_SELECTION':
        return {
          type: 'video',
          src: selectedGrade === 'grade5'
            ? `${process.env.PUBLIC_URL}/assets/backgrounds/menu-grade-5-bg.mp4`
            : `${process.env.PUBLIC_URL}/assets/backgrounds/menu-grade-6-bg.mp4`
        };
      case 'CHARACTER_SELECTION':
        return { type: 'image', src: `${process.env.PUBLIC_URL}/assets/backgrounds/teachers-bg.jpeg` };
      case 'CONVERSATION':
      case 'ENDED':
        return { type: 'image', src: `${process.env.PUBLIC_URL}/assets/backgrounds/chat-bg.jpeg` };
      default:
        return { type: 'none' };
    }
  };
  // ========= END: App.js - getBackgroundConfig Function (REPLACEMENT) =========

  // ========= START: App.js - Main Component Return (REPLACEMENT) =========
  return (
    <div className="App" data-phase={appPhase}>
      <Background background={getBackgroundConfig()} />

      {/* MODIFIED: The check for 'PRONUNCIATION_CHALLENGE' is removed. */}
      {playerData && appPhase !== 'WELCOME' && (
        <header className="App-header">
          {(appPhase !== 'CHARACTER_SELECTION' && appPhase !== 'WELCOME') && (
            <button onClick={handleBack} className="back-button">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M15.41 7.41L14 6L8 12L14 18L15.41 16.59L10.83 12L15.41 7.41Z" fill="currentColor" />
              </svg>
            </button>
          )}
          {appPhase === 'CHARACTER_SELECTION' && (
            <button onClick={handleBack} className="back-button">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M15.41 7.41L14 6L8 12L14 18L15.41 16.59L10.83 12L15.41 7.41Z" fill="currentColor" />
              </svg>
              Back
            </button>
          )}
          <div className="header-title-container"><h1>BP ECHO</h1></div>
          <div className="header-buttons-right">
            {(appPhase === 'CONVERSATION' || appPhase === 'ENDED' || appPhase === 'BP_ECHO_MAIN_MENU' || appPhase === 'GRADE_SELECTION') && (
              <button onClick={handleToggleMute} className="mute-button" title={isMuted ? "Unmute" : "Mute"}>
                {isMuted ? (
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg"><path d="M16.5 12C16.5 10.23 15.48 8.71 14 7.97V10.18L16.45 12.63C16.48 12.43 16.5 12.22 16.5 12ZM19 12C19 12.82 18.87 13.61 18.64 14.34L20.05 15.75C20.68 14.63 21 13.36 21 12C21 7.72 18.28 4.21 14 3.23V5.29C17.18 6.25 19 8.83 19 12ZM4.27 3L3 4.27L7.73 9H3V15H7L12 20V13.27L16.25 17.52C15.58 17.83 14.83 18.06 14 18.18V20.24C15.38 19.93 16.63 19.26 17.68 18.36L19.73 20.41L21 19.14L4.27 3ZM12 4L9.91 6.09L12 8.18V4Z" /></svg>
                ) : (
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg"><path d="M3 9V15H7L12 20V4L7 9H3ZM16.5 12c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02zM14 4.45v.2c0 .38.25.71.6.85C17.18 6.53 19 9.06 19 12s-1.82 5.47-4.4 6.5c-.35.14-.6.47-.6.85v.2c0 .63.63 1.07 1.21.85C18.6 19.11 21 15.84 21 12s-2.4-7.11-5.79-8.4c-.58-.23-1.21.22-1.21.85z" /></svg>
                )}
              </button>
            )}
            {/* REMOVED: The "Character" button linked to the pronunciation game is gone. */}
            {(appPhase === 'BP_ECHO_MAIN_MENU' || appPhase === 'CONVERSATION' || appPhase === 'ENDED' || appPhase === 'GRADE_SELECTION') && (
              <button onClick={showCharacterSelection} className="teacher-button">Teacher</button>
            )}
          </div>
        </header>
      )}
      <main className="main-content-area">
        {!speechRecognition.hasRecognitionSupport ? (
          <p>Speech Recognition not supported. Please use Chrome or Safari.</p>
        ) : (
          renderContent()
        )}
      </main>
    </div>
  );
  // ========= END: App.js - Main Component Return (REPLACEMENT) =========
}

export default App;
// ========= END: App.js (Corrected with Anti-Echo Logic) =========